import { useDrag } from "@use-gesture/react";
import classNames from "classnames";
import EventEmitter from "eventemitter3";
import { action, computed, reaction, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import { atom } from "../atom";
import { appSettingsMobx, uiStateMobx } from "../SharedIframeState";
import { PaneType, Position, Rect } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import { getThemeColor, isSafari, useSyncRef } from "../Utils";
import {
  ClipboardPane as ClipboardPaneType,
  ClipboardSelection,
  clipboardSelectionAtom,
  pasteClipboard,
  removeClipboardSelection,
} from "./CanvasClipboard";
import {
  canvasTransformComputed,
  canvasViewport,
  focusedPaneIdAtom,
  selectedPaneIdsAtom,
} from "./CanvasState";
import { Wire } from "./PaneInput";
import {
  convertViewportPositionToCanvasOffset,
  getPaneIdFromPaneInput,
  getRectForCollapsedPane,
  isPaneCollapsed,
  OVERLAY_PANE_CLASSNAME,
  OVERLAY_PANE_HEADER_CLASSNAME,
  positionDistance,
  roundPositionToGrid,
} from "./SandboxUtils";

export const clipboardDragSelectionIdAtom = atom<number | undefined>(undefined);

const dragShield = document.createElement("div");
dragShield.className =
  "fixed top-0 left-0 right-0 bottom-0 z-99999 cursor-pointer";
export const CLIPBOARD_PASTE_ID = -1;
export const DragClipboardPasteStartEventEmitter = new EventEmitter();

function getRectForClipboardPane(p: ClipboardPaneType): Rect {
  return isPaneCollapsed(p)
    ? getRectForCollapsedPane(p.rect, p.type, p.name)
    : p.rect;
}

export function DragClipboardPastePreview() {
  const [clipboardSelection, setClipboardSelection] = useState<
    ClipboardSelection | undefined
  >(undefined);
  const [position, setPosition] = useState<Position | undefined>();
  const positionRef = useSyncRef(position);

  const [shouldShowPasteNotice, setShouldSetPasteNotice] = useState(false);

  const dragClipboardCleanupRef = useRef<(() => void) | undefined>();
  useEffect(
    () => () => {
      if (dragClipboardCleanupRef.current !== undefined) {
        dragClipboardCleanupRef.current();
      }
    },
    []
  );
  useEffect(() => {
    function onMouseDown({
      clipboardSelection,
      clientPosition,
      isDuplicate,
    }: {
      clipboardSelection: ClipboardSelection;
      clientPosition: Position;
      isDuplicate?: boolean;
    }) {
      let didMoveMouse = false;
      const startMousePosition: Position = [
        clientPosition[0],
        clientPosition[1],
      ];
      if (clipboardSelection.id === CLIPBOARD_PASTE_ID) {
        setShouldSetPasteNotice(true);
      }
      if (isSafari) {
        document.body.classList.add("safari-is-dragging-clipboard");
      }
      function onMouseMove(e: MouseEvent) {
        runInAction(() => {
          const newPosition = roundPositionToGrid(
            convertViewportPositionToCanvasOffset(
              [e.clientX, e.clientY],
              canvasTransformComputed.get(),
              canvasViewport.zoom
            ),
            1
          );
          setPosition((position) => {
            if (
              position !== undefined &&
              position[0] === newPosition[0] &&
              position[1] === newPosition[1]
            ) {
              return position;
            }
            return newPosition;
          });
          if (!didMoveMouse) {
            if (
              positionDistance(startMousePosition, [e.clientX, e.clientY]) < 5
            ) {
              return;
            }
            didMoveMouse = true;
            setClipboardSelection(clipboardSelection);
            if (isDuplicate) {
              dragShield.style.cursor = "grabbing";
            }
            document.body.appendChild(dragShield);
            clipboardDragSelectionIdAtom.value = clipboardSelection.id;
            focusedPaneIdAtom.value = undefined;
            selectedPaneIdsAtom.value = [];
          }
        });
      }
      function cleanup() {
        if (isSafari) {
          document.body.classList.remove("safari-is-dragging-clipboard");
        }
        if (didMoveMouse) {
          document.body.removeChild(dragShield);
          dragShield.style.cursor = "";
        }
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
        window.removeEventListener("keydown", onKeyDown);
        dragClipboardCleanupRef.current = undefined;
        setClipboardSelection(undefined);
        setPosition(undefined);
        setShouldSetPasteNotice(false);
        if (clipboardDragSelectionIdAtom.value === clipboardSelection.id) {
          runInAction(() => {
            clipboardDragSelectionIdAtom.value = undefined;
          });
        }
      }
      function onMouseUp() {
        if (didMoveMouse) {
          const position = positionRef.current!;
          pasteClipboard(clipboardSelection, position);
        }
        cleanup();
      }
      function onKeyDown(e: KeyboardEvent) {
        if (e.key === "Escape") {
          cleanup();
        }
      }
      dragClipboardCleanupRef.current = cleanup;
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
      window.addEventListener("keydown", onKeyDown);
    }
    DragClipboardPasteStartEventEmitter.addListener("mousedown", onMouseDown);
    return () => {
      DragClipboardPasteStartEventEmitter.removeListener(
        "mousedown",
        onMouseDown
      );
    };
  });

  const canvasTransform = canvasTransformComputed.get();
  return (
    <>
      {shouldShowPasteNotice ? (
        <div className="fixed top-12 left-4 bg-neutral-800 text-neutral-0 p-4 rounded z-10">
          use mouse to paste. esc to cancel
        </div>
      ) : null}
      {clipboardSelection !== undefined && position !== undefined ? (
        <div className="fixed top-0 bottom-0 left-0 right-0 z-99999">
          <div
            className="absolute"
            style={{
              transform: `translate(${canvasTransform[0]}px, ${canvasTransform[1]}px) scale(${canvasViewport.zoom}) translate(${position[0]}px, ${position[1]}px)`,
            }}
          >
            {clipboardSelection.panes.map((p, i) => {
              const paneRect = getRectForClipboardPane(p);
              return (
                <React.Fragment key={i}>
                  {"inputs" in p
                    ? p.inputs.map((input, inputIndex) => {
                        if (input.source === undefined) {
                          return null;
                        }
                        const inputPaneId = input.source[0];
                        if (inputPaneId.startsWith("#")) {
                          const inputPaneRect = getRectForClipboardPane(
                            clipboardSelection.panes[
                              parseInt(inputPaneId.substring(1))
                            ]
                          );
                          return (
                            <Wire
                              from={[
                                inputPaneRect[0] + inputPaneRect[2],
                                inputPaneRect[1],
                              ]}
                              to={[p.rect[0], p.rect[1]]}
                              key={inputIndex}
                            />
                          );
                        }
                      })
                    : null}
                  <div
                    className="absolute bg-neutral-0 bg-opacity-75 border border-primary-400 rounded z-99999"
                    style={{
                      left: `${paneRect[0]}px`,
                      top: `${paneRect[1]}px`,
                      width: `${paneRect[2] + 1}px`,
                      height: `${paneRect[3] + 1}px`,
                    }}
                  ></div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      ) : null}
    </>
  );
}

const PREVIEW_CANVAS_WIDTH = 200;
const PREVIEW_CANVAS_HEIGHT = 140;
const PREVIEW_CANVAS_PADDING = 96;
const ClipboardSelectionPreview = observer(
  ({ clipboardSelection }: { clipboardSelection: ClipboardSelection }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    useEffect(
      () =>
        reaction(
          () => appSettingsMobx.theme,
          () => {
            const canvasElement = canvasRef.current!;
            const maxPaneRight = Math.max(
              ...clipboardSelection.panes.map((p) => p.rect[0] + p.rect[2])
            );
            const maxPaneBottom = Math.max(
              ...clipboardSelection.panes.map((p) => p.rect[1] + p.rect[3])
            );
            let xOffset = PREVIEW_CANVAS_PADDING;
            let yOffset = PREVIEW_CANVAS_PADDING;
            const xScale =
              PREVIEW_CANVAS_WIDTH /
              (maxPaneRight + 2 * PREVIEW_CANVAS_PADDING);
            const yScale =
              PREVIEW_CANVAS_HEIGHT /
              (maxPaneBottom + 2 * PREVIEW_CANVAS_PADDING);
            let scale: number;
            // choose the smaller scale and center on cross-axis
            if (xScale < yScale) {
              scale = xScale;
              yOffset +=
                (PREVIEW_CANVAS_HEIGHT / xScale - maxPaneBottom) / 2 -
                PREVIEW_CANVAS_PADDING;
            } else {
              scale = yScale;
              xOffset +=
                (PREVIEW_CANVAS_WIDTH / yScale - maxPaneRight) / 2 -
                PREVIEW_CANVAS_PADDING;
            }
            const ctx = canvasElement.getContext("2d")!;
            ctx.save();
            ctx.scale(2, 2);
            ctx.lineWidth = 1;
            function transformX(x: number) {
              return Math.round((x + xOffset) * scale);
            }
            function transformY(y: number) {
              return Math.round((y + yOffset) * scale);
            }
            ctx.fillStyle = "#fcfcfb";
            ctx.strokeStyle = `rgb(${getThemeColor("--color-primary-200")})`;
            clipboardSelection.panes.forEach((p) => {
              const inputPaneIds = new Set<string>();
              if ("inputs" in p) {
                p.inputs.forEach((paneInput) => {
                  const inputPaneId = getPaneIdFromPaneInput(paneInput);
                  if (inputPaneId !== undefined) {
                    inputPaneIds.add(inputPaneId);
                  }
                });
              }
              if (p.type === PaneType.EvaluateTemplate) {
                Object.values(p.templateInputSources).forEach(([paneId]) => {
                  inputPaneIds.add(paneId);
                });
              }
              inputPaneIds.forEach((inputPaneId) => {
                const inputPane = inputPaneId.startsWith("#")
                  ? clipboardSelection.panes[parseInt(inputPaneId.slice(1))]
                  : undefined;
                if (inputPane === undefined) {
                  return;
                }
                const inputPaneRect = getRectForClipboardPane(inputPane);
                ctx.beginPath();
                ctx.moveTo(
                  transformX(inputPaneRect[0] + inputPaneRect[2]),
                  transformY(inputPaneRect[1])
                );
                ctx.lineTo(transformX(p.rect[0]), transformY(p.rect[1]));
                ctx.stroke();
              });
            });
            ctx.fillStyle = `rgb(${getThemeColor("--color-primary-50")})`;
            ctx.strokeStyle = `rgb(${getThemeColor("--color-primary-300")})`;
            clipboardSelection.panes.forEach((p) => {
              ctx.beginPath();
              const rect = getRectForClipboardPane(p);
              ctx.rect(
                transformX(rect[0]),
                transformY(rect[1]),
                Math.round(rect[2] * scale),
                Math.round(rect[3] * scale)
              );
              ctx.fill();
              ctx.stroke();
            });
            ctx.restore();
          },
          { fireImmediately: true }
        ),
      []
    );

    const isDragging = computed(
      () => clipboardDragSelectionIdAtom.value === clipboardSelection.id
    ).get();

    return (
      <div className="relative">
        <canvas
          width={`${PREVIEW_CANVAS_WIDTH * 2}px`}
          height={`${PREVIEW_CANVAS_HEIGHT * 2}px`}
          style={{
            width: `${PREVIEW_CANVAS_WIDTH}px`,
            height: `${PREVIEW_CANVAS_HEIGHT}px`,
          }}
          ref={canvasRef}
        ></canvas>
        <div
          className={classNames(
            "absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center bg-primary-50 text-primary-500 bg-opacity-80 hover:opacity-100 cursor-pointer",
            isDragging ? "opacity-100" : "opacity-0 hover:opacity-100"
          )}
          onMouseDown={(e) => {
            if (e.button !== 0) {
              return;
            }
            DragClipboardPasteStartEventEmitter.emit("mousedown", {
              clipboardSelection,
              clientPosition: [e.clientX, e.clientY],
            });
            e.preventDefault();
          }}
        >
          {isDragging ? "press esc to cancel" : "drag to paste"}
        </div>
      </div>
    );
  }
);

const ClipboardPaneImpl = observer(
  ({ clipboardSelections }: { clipboardSelections: ClipboardSelection[] }) => {
    const hasMaximizedPane = computed(
      () => uiStateMobx.maximizedPaneId !== undefined
    ).get();
    const rootRef = useRef<HTMLDivElement | null>(null);
    const offsetRef = useRef<Position>([0, 0]);

    const bindDrag = useDrag(
      ({ target, first, last, offset }) => {
        offsetRef.current = offset;
        rootRef.current!.style.transform = `translate(${offset[0]}px, ${offset[1]}px)`;
        if (first && target instanceof HTMLElement) {
          target.style.cursor = "grabbing";
        }
        if (last && target instanceof HTMLElement) {
          target.style.cursor = "";
        }
      },
      { from: () => offsetRef.current }
    );

    const [didMount, setDidMount] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(true);
    useEffect(() => {
      const mountTimeout = setTimeout(() => {
        setDidMount(true);
      }, 0);
      const animateTimeout = setTimeout(() => {
        setShouldAnimate(false);
      }, 300);
      return () => {
        clearTimeout(mountTimeout);
        clearTimeout(animateTimeout);
      };
    }, []);

    return (
      <div
        className={classNames(
          OVERLAY_PANE_CLASSNAME,
          "bg-neutral-0 fixed top-16 right-4 flex flex-col rounded overflow-hidden shadow-xl transform",
          hasMaximizedPane ? "-z-1" : "z-1",
          !didMount ? "translate-x-8 opacity-0" : "translate-x-0 opacity-100",
          {
            transition: shouldAnimate,
          }
        )}
        ref={rootRef}
      >
        <div
          {...bindDrag()}
          className={classNames(
            OVERLAY_PANE_HEADER_CLASSNAME,
            "h-8 px-2 flex items-center justify-between cursor-grab touch-none"
          )}
        >
          <div className="font-medium">clipboard</div>
          <Tooltip content="clear clipboard">
            <button
              onClick={action(() => {
                clipboardSelectionAtom.value = [];
              })}
              className="flex p-0.5 -mr-1 border rounded-sm border-neutral-0 border-opacity-0 hover:border-opacity-50"
            >
              <FeatherIcon icon="trash" size="sm" />
            </button>
          </Tooltip>
        </div>
        <div className="max-h-[360px] overflow-auto">
          {clipboardSelections.map((clipboardSelection, i) => (
            <div
              className={classNames(
                "relative",
                i !== clipboardSelections.length - 1
                  ? "border-b border-neutral-150"
                  : undefined
              )}
              key={clipboardSelection.id}
            >
              <ClipboardSelectionPreview
                clipboardSelection={clipboardSelection}
              />
              <button
                className="absolute top-0 right-0 flex p-1.5 text-neutral-500 opacity-50 hover:opacity-100 transition-opacity"
                onClick={() => {
                  removeClipboardSelection(clipboardSelection.id);
                }}
              >
                <FeatherIcon icon="x" size="sm" />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

export const ClipboardPane = observer(({}: {}) => {
  const clipboardSelections = clipboardSelectionAtom.value;

  if (clipboardSelections.length === 0) {
    return null;
  }

  return <ClipboardPaneImpl clipboardSelections={clipboardSelections} />;
});
